import React from 'react';

// constants
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  DropdownMenuContentProps,
  DropdownMenuItemProps,
  DropdownMenuLabelProps,
  DropdownMenuPortalProps,
} from '@radix-ui/react-dropdown-menu';
import { sizes } from '../popover/style';

// components
import PopoverContentContainer from '../popover/popover-content-container';
import SlideDownMount from '../../transitions/slide-down-mount';

// types
import { ButtonProps } from '../button';
import MenuToggle from './menu-toggle';
import { AccessibleMenuItem, AccessibleMenuItemProps } from './menu-item';

export interface MenuPopoverProps {
  className?: string;
  align?: 'start' | 'end' | 'center';
  children?: React.ReactNode | ((props: { onOpenChange: (isOpen: boolean) => void }) => React.ReactNode);
  size?: keyof typeof sizes;
  open?: boolean;
  modal?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export const MenuContext = React.createContext<{
  open: boolean;
  size?: keyof typeof sizes;
  align?: 'start' | 'end' | 'center';
}>({
  open: false,
  align: 'end',
});

const MenuPopoverComponent = ({
  children,
  size = 'sm',
  align = 'end',
  open: openProp,
  modal = false,
  onOpenChange: onOpenChangeProp,
  ...rest
}: MenuPopoverProps): ReturnType<typeof DropdownMenu.Root> => {
  const [open, setOpen] = React.useState(openProp || false);

  const onOpenChange = (open: boolean) => {
    onOpenChangeProp?.(open);
    setOpen(open);
  };

  return (
    <DropdownMenu.Root {...rest} modal={modal} onOpenChange={onOpenChange} open={open}>
      <MenuContext value={{ open, size, align }}>
        <React.Fragment>{typeof children === 'function' ? children({ onOpenChange }) : children}</React.Fragment>
      </MenuContext>
    </DropdownMenu.Root>
  );
};

const MenuPopover: React.FunctionComponent<MenuPopoverProps> & {
  Items: React.FunctionComponent<DropdownMenuContentProps>;
  Button: React.FunctionComponent<MenuButtonProps>;
  MenuToggle: typeof MenuToggle;
  Item: React.FunctionComponent<AccessibleMenuItemProps>;
  CleanItemsContainer: React.FunctionComponent<DropdownMenuContentProps>;
  CleanItem: React.FunctionComponent<DropdownMenuItemProps>;
  Label: React.FunctionComponent<DropdownMenuLabelProps>;
  Portal: React.FunctionComponent<DropdownMenuPortalProps>;
} = (props: MenuPopoverProps): React.JSX.Element => <MenuPopoverComponent {...props} />;

type MenuButtonProps = { as?: any } & ButtonProps;
const MenuPopoverButton = ({ as: Component, disabled, ...rest }: MenuButtonProps) => {
  const menuContext = React.use(MenuContext);
  if (!Component) {
    return <DropdownMenu.Trigger asChild {...rest} />;
  }

  return (
    <DropdownMenu.Trigger asChild disabled={disabled}>
      <Component {...rest} disabled={disabled} active={menuContext.open} />
    </DropdownMenu.Trigger>
  );
};

const MenuPopoverItems = ({ children, onClick, style, ...rest }: DropdownMenuContentProps) => {
  const menuContext = React.use(MenuContext);

  return (
    <DropdownMenu.Content
      onClick={onClick}
      sideOffset={4}
      style={{ zIndex: 10000 }}
      align={menuContext.align}
      {...rest}
    >
      <SlideDownMount>
        <PopoverContentContainer padding={'xs'} size={menuContext.size} style={style}>
          {children}
        </PopoverContentContainer>
      </SlideDownMount>
    </DropdownMenu.Content>
  );
};

MenuPopover.CleanItemsContainer = DropdownMenu.Content;
MenuPopover.Items = MenuPopoverItems;
MenuPopover.Button = MenuPopoverButton;
MenuPopover.MenuToggle = MenuToggle;
MenuPopover.Item = AccessibleMenuItem;
MenuPopover.CleanItem = DropdownMenu.Item;
MenuPopover.Label = DropdownMenu.Label;
MenuPopover.Portal = DropdownMenu.Portal;

export default MenuPopover;
